import { useMemo } from "react";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";

export interface DecoratedFields {
  amountPrecision: number;
  pricePrecision: number;
}

interface ResolvableLike {
  instrument_name?: string;
  asset?: string;
  instrument_type?: string;
}

/**
 * Decorates each history item with `amountPrecision` / `pricePrecision`
 * resolved from the instruments registry, and replaces `instrument_name`
 * with the canonical display name when one is available. Used by all
 * history tables so precision logic lives in one place.
 */
export function useDecoratedHistoryItems<T extends ResolvableLike>(
  items: T[]
): Array<T & DecoratedFields> {
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();

  return useMemo(
    () =>
      items.map((item) => {
        const precision = resolveMarketPrecision(item as any);
        return {
          ...item,
          instrument_name:
            resolveInstrumentName(item as any) || item.instrument_name,
          amountPrecision: precision.amount_precision,
          pricePrecision: precision.price_precision,
        };
      }),
    [items, resolveInstrumentName, resolveMarketPrecision]
  );
}
